import type { ChartResult } from '../domain/types';
import { readingProfileFor } from './readingProfiles';

export interface GroundedChartFact {
  label: string;
  value: string;
  section?: string;
}

const QUESTION_FACT_RULES: Array<{ question: RegExp; terms: string[] }> = [
  {
    question: /什么时候|多久|哪天|何时|时间|时机|月份|日期|应期/,
    terms: ['应期', '时间', '时机', '流年', '流月', '大运', '月建', '日辰', '旬空', '节气', '日期', '时令', '值日', '起运']
  },
  {
    question: /工作|求职|跳槽|职业|事业|面试|录用/,
    terms: ['工作', '求职', '职业', '事业', '官禄', '迁移', '官鬼', '父母', '财帛']
  },
  {
    question: /钱|收入|财运|投资|生意|赚钱|亏损/,
    terms: ['财', '财帛', '妻财', '收入', '经营', '生意']
  },
  {
    question: /感情|恋爱|婚姻|对象|伴侣|复合/,
    terms: ['感情', '婚姻', '夫妻', '夫妻宫', '妻财', '官鬼', '桃花']
  },
  {
    question: /出门|出行|行程|方向|方位|去哪|路上/,
    terms: ['出行', '迁移', '驿马', '方位', '方向', '宫位', '门']
  },
  {
    question: /为什么|原因|依据|怎么判断|怎么看出/,
    terms: ['动爻', '变卦', '用神', '世应', '体用', '旺衰', '生克', '格局', '主客', '关系']
  },
  {
    question: /接下来|下一步|变化|趋势|怎么做|做什么/,
    terms: ['动爻', '变卦', '应爻', '变化', '趋势', '体用', '世应', '主客']
  }
];

function collectChartFacts(chart: ChartResult): GroundedChartFact[] {
  const facts: GroundedChartFact[] = [
    ...chart.summary.map((fact) => ({ label: fact.label, value: fact.value })),
    ...chart.sections.flatMap((section) => section.facts.map((fact) => ({
      label: fact.label,
      value: fact.value,
      section: section.title
    })))
  ];
  if (!facts.length && chart.headline.trim()) {
    facts.push({ label: '排盘标题', value: chart.headline.trim() });
  }
  const seen = new Set<string>();
  return facts.filter((fact) => {
    const key = `${fact.label}\u0000${fact.value}`;
    if (!fact.value.trim() || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** 根据当前问题给盘面字段排序，避免每次机械地拿前四项。 */
export function selectRelevantChartFacts(chart: ChartResult, question: string, limit = 4): GroundedChartFact[] {
  const rules = QUESTION_FACT_RULES.filter((rule) => rule.question.test(question));
  const moduleTerms = readingProfileFor(chart.moduleId).factTerms;
  const scored = collectChartFacts(chart).map((fact, index) => {
    const searchable = `${fact.section ?? ''}${fact.label}${fact.value}`;
    const relevance = rules.reduce((score, rule) => (
      score + (rule.terms.some((term) => searchable.includes(term)) ? 20 : 0)
    ), 0);
    const moduleWeight = moduleTerms.some((term) => searchable.includes(term)) ? 8 : 0;
    const summaryWeight = fact.section ? 0 : 3;
    return { fact, index, score: relevance + moduleWeight + summaryWeight };
  });
  return scored
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, Math.max(1, limit))
    .map(({ fact }) => fact);
}

export function formatPromptFact(fact: GroundedChartFact): string {
  return `${fact.section ? `${fact.section}·` : ''}${fact.label}：${fact.value}`;
}

const PLAIN_EXPLANATIONS: Record<string, string> = {
  本卦: '主要卦象',
  变卦: '变化后的卦象',
  动爻: '发生变化的爻位',
  世爻: '代表自身的位置',
  应爻: '代表对方或外部的位置',
  世应: '自身与外部的对应关系',
  用神: '本次问题重点观察的对象',
  体卦: '代表自身或主体',
  用卦: '代表事情或外部条件',
  应期: '程序给出的时间线索',
  旬空: '当前暂时难以落实的位置',
  值使: '当前主导事情推进方式的门',
  三传: '事情从开始到后续的传递线索',
  日主: '八字中代表自身的核心',
  命宫: '紫微盘中观察自身处境的主宫',
  建除: '黄历当天所处的十二值日阶段',
  司天: '五运六气中主导上半年气候的线索'
};

export function describeGroundedFact(fact: GroundedChartFact): string {
  const explanation = PLAIN_EXPLANATIONS[fact.label];
  return `${fact.label}“${fact.value}”${explanation ? `（${explanation}）` : ''}`;
}
